import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowUpRight, ExternalLink, X } from "lucide-react";
import SEO from "../components/SEO";
import PageHeader from "../components/PageHeader";
import EmptyState from "../components/EmptyState";
import { Reveal } from "../components/Reveal";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import api from "../lib/api";
import { dayMonth, fmtDate, TEAM_LABELS } from "../lib/format";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const TEAM_FILTERS = [
  { value: "", label: "Toutes" },
  { value: "feminine", label: "Féminine N1/UCI" },
  { value: "masculine", label: "Homme N3" },
  { value: "jeunes", label: "École de Vélo" },
  { value: "loisir", label: "Loisir et Cyclo" },
];

const MEDALS = { 1: "🥇", 2: "🥈", 3: "🥉" };

const placeNum = (place) => {
  const n = parseInt(String(place).replace(/[^\d]/g, ""), 10);
  return Number.isNaN(n) ? null : n;
};

const medal = (place) => MEDALS[placeNum(place)] || null;

const riderName = (c) => `${c.prenom ? `${c.prenom} ` : ""}${c.nom}`.trim();

function ResultCard({ result, onOpen, index }) {
  const d = dayMonth(result.date);
  const top = (result.coureurs || []).slice(0, 4);
  return (
    <Reveal delay={Math.min(index, 5) * 0.05}>
      <button
        type="button"
        onClick={() => onOpen(result)}
        data-testid={`result-card-${result.id}`}
        className="group w-full text-left bg-white border border-navy/10 hover:border-rouge hover:shadow-[0_12px_40px_-12px_rgba(10,17,40,0.25)] transition-[border-color,box-shadow] duration-300 flex flex-col h-full"
      >
        <div className="flex items-stretch">
          <div className="shrink-0 w-[76px] flex flex-col items-center justify-center py-5 bg-navy text-white group-hover:bg-rouge transition-colors duration-300">
            <span className="font-heading font-semibold text-2xl leading-none">{d.day}</span>
            <span className="text-[10px] uppercase tracking-widest mt-1.5 opacity-80">{d.month}</span>
            <span className="text-[10px] uppercase tracking-widest opacity-60">{d.year}</span>
          </div>
          <div className="min-w-0 flex-1 p-5">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-heading uppercase font-semibold tracking-tight text-navy text-lg leading-snug group-hover:text-rouge transition-colors duration-300">
                {result.name}
              </h3>
              <ArrowUpRight size={18} className="shrink-0 mt-1 text-navy/30 group-hover:text-rouge transition-[color,transform] duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </div>
            <p className="mt-1.5 flex items-center gap-1.5 text-sm text-steel">
              <MapPin size={13} className="text-rouge shrink-0" />
              <span className="truncate">{result.location || ""}</span>
            </p>
            <span className="mt-3 inline-block uppercase tracking-wider text-[11px] font-semibold text-navy/60 border border-navy/15 px-2.5 py-1">
              {result.categorie || TEAM_LABELS[result.team] || result.team}
            </span>
            {result.a_venir && top.length === 0 && (
              <p className="mt-4 pt-3 border-t border-navy/10 text-sm text-steel" data-testid={`result-avenir-${result.id.slice(0, 8)}`}>
                Résultats à venir
              </p>
            )}
            {top.length > 0 && (
              <div className="mt-4 pt-3 border-t border-navy/10 space-y-1.5">
                {top.map((c, i) => (
                  <p key={i} className="text-sm text-navy/85 flex items-center gap-2">
                    {medal(c.place) && <span className="text-base leading-none">{medal(c.place)}</span>}
                    <span className="font-semibold">{riderName(c)}</span>
                    <span className="text-steel">— {c.place}</span>
                  </p>
                ))}
              </div>
            )}
            {result.resultat_equipe && (
              <p className="mt-3 text-[11px] font-heading uppercase tracking-wider text-rouge font-bold" data-testid={`result-team-${result.id.slice(0, 8)}`}>
                Résultat par équipe : {result.resultat_equipe}
              </p>
            )}
          </div>
        </div>
      </button>
    </Reveal>
  );
}

function ResultDetail({ result, onClose }) {
  const open = Boolean(result);
  const [portrait, setPortrait] = useState(false);

  useEffect(() => {
    setPortrait(false);
  }, [result?.photo]);

  const photoSrc = result?.photo
    ? result.photo.startsWith("/assets") || result.photo.startsWith("http")
      ? result.photo
      : `${API_BASE}/api/files/${result.photo}`
    : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-0 bg-white border-navy/10 sm:rounded-none"
        data-testid="result-detail-dialog"
        aria-describedby={undefined}
      >
        {result && (
          <div>
            {photoSrc && (
              <img
                src={photoSrc}
                alt=""
                aria-hidden="true"
                className="hidden"
                onLoad={(e) => setPortrait(e.target.naturalHeight > e.target.naturalWidth)}
              />
            )}
            {photoSrc && (
              <div className={`bg-navy overflow-hidden flex items-center justify-center ${portrait ? "" : "aspect-[16/9]"}`}>
                <img
                  src={photoSrc}
                  alt={result.name}
                  className={portrait ? "w-full h-auto max-h-[60vh] object-contain" : "w-full h-full object-cover"}
                  data-testid="result-detail-photo"
                />
              </div>
            )}
            <div className="p-6 sm:p-10">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="bg-navy text-white font-heading uppercase tracking-wider text-xs px-3 py-1.5" data-testid="result-detail-date">{fmtDate(result.date)}</span>
                <span className="bg-rouge/10 text-rouge font-heading uppercase tracking-wider text-xs px-3 py-1.5" data-testid="result-detail-team">{result.categorie || TEAM_LABELS[result.team] || result.team}</span>
                {result.location && (
                  <span className="text-sm text-steel flex items-center gap-1.5"><MapPin size={13} className="text-rouge" />{result.location}</span>
                )}
              </div>
              <DialogTitle className="font-heading uppercase font-semibold tracking-tight text-navy text-2xl sm:text-4xl leading-tight" data-testid="result-detail-name">
                {result.name}
              </DialogTitle>

              {result.a_venir && (!result.coureurs || result.coureurs.length === 0) && (
                <p className="mt-6 bg-mist border-l-2 border-rouge p-5 text-sm text-navy/85" data-testid="result-detail-avenir">
                  Résultats à venir — les places des coureurs et coureuses du club seront publiées ici dès qu'elles seront saisies.
                </p>
              )}

              {result.coureurs?.length > 0 && (
                <div className="mt-8" data-testid="result-detail-table">
                  <p className="font-heading uppercase tracking-wider text-[11px] text-steel mb-3">Résultats du club</p>
                  <div className="border border-navy/10">
                    <div className="grid grid-cols-[70px_1fr] sm:grid-cols-[90px_1fr_160px] bg-navy text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.15em] font-bold">
                      <span>Place</span><span>Coureur / Coureuse</span><span className="hidden sm:block">Équipe</span>
                    </div>
                    {result.coureurs.map((c, i) => (
                      <div key={i} className={`grid grid-cols-[70px_1fr] sm:grid-cols-[90px_1fr_160px] px-4 py-3 items-center text-sm ${i % 2 ? "bg-mist" : "bg-white"}`} data-testid={`result-rider-${i}`}>
                        <span className="font-heading font-semibold text-lg text-navy flex items-center gap-1.5">
                          {medal(c.place) && <span>{medal(c.place)}</span>}
                          {c.place}
                        </span>
                        <span className="text-navy font-medium">
                          {riderName(c)}
                          {(c.temps || c.ecart) && (
                            <span className="block text-xs text-steel font-normal">{[c.temps, c.ecart].filter(Boolean).join(" · ")}</span>
                          )}
                        </span>
                        <span className="hidden sm:block text-xs text-steel uppercase tracking-wider">{TEAM_LABELS[result.team] || result.team}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.resultat_equipe && (
                <div className="mt-6 bg-navy text-white px-6 py-5 flex items-center justify-between" data-testid="result-detail-team-result">
                  <span className="font-heading uppercase tracking-wider text-xs text-white/60">Résultat par équipe</span>
                  <span className="font-heading uppercase font-semibold text-2xl text-rouge">{result.resultat_equipe}</span>
                </div>
              )}

              {result.a_retenir && (
                <div className="mt-6 bg-mist border-l-2 border-rouge p-5" data-testid="result-detail-highlight">
                  <p className="font-heading uppercase tracking-wider text-[11px] text-steel mb-2">À retenir</p>
                  <p className="text-navy/85 text-sm leading-relaxed whitespace-pre-line">{result.a_retenir}</p>
                </div>
              )}

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                {result.article_slug && (
                  <Link
                    to={`/actualites/${result.article_slug}`}
                    data-testid="result-detail-article-link"
                    className="inline-flex items-center justify-center gap-2.5 bg-navy text-white font-heading uppercase tracking-wider text-sm px-7 py-4 hover:bg-rouge transition-colors duration-300"
                  >
                    Lire l'article complet
                    <ArrowUpRight size={15} />
                  </Link>
                )}
                {result.lien_classement && (
                  <a
                    href={result.lien_classement}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="result-detail-ranking-link"
                    className="inline-flex items-center justify-center gap-2.5 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-7 py-4 hover:bg-navy transition-colors duration-300"
                  >
                    Voir le classement complet
                    <ExternalLink size={15} />
                  </a>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  data-testid="result-detail-close"
                  className="inline-flex items-center justify-center gap-2 border border-navy/25 text-navy font-heading uppercase tracking-wider text-sm px-7 py-4 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300"
                >
                  <X size={15} /> Fermer
                </button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Resultats() {
  const [results, setResults] = useState(null);
  const [team, setTeam] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const q = team ? `?team=${team}` : "";
    api.get(`/results${q}`).then((r) => setResults(r.data)).catch(() => setResults([]));
  }, [team]);

  return (
    <>
      <SEO title="Résultats" description="Résultats des coureurs et coureuses du Club Cycliste Le Boulou en compétition." />
      <PageHeader kicker="Performance" title="Résultats" />

      <section className="py-16 sm:py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="flex flex-wrap gap-3 mb-12" data-testid="results-filters">
              {TEAM_FILTERS.map((t) => (
                <button
                  key={t.value}
                  data-testid={`results-filter-${t.value || "all"}`}
                  onClick={() => setTeam(t.value)}
                  className={`font-heading uppercase tracking-wider text-xs px-4 py-2.5 border transition-[background-color,color,border-color] duration-300 ${
                    team === t.value ? "bg-navy border-navy text-white" : "border-navy/20 text-navy/70 hover:border-rouge hover:text-rouge"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </Reveal>

          {results === null ? null : results.length === 0 ? (
            <EmptyState
              title="Aucun résultat publié"
              message="Les résultats des coureurs et coureuses du club seront publiés ici au fil des compétitions."
              testId="results-empty"
            />
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="results-grid">
              {results.map((r, i) => (
                <ResultCard key={r.id} result={r} onOpen={setSelected} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      <ResultDetail result={selected} onClose={() => setSelected(null)} />
    </>
  );
}
